import { Roles, tokenContainsRole } from '@/lib/context';
import {
  useGetAggregateGewasReport,
  useGetAggregateOtherReport,
  useGetBevoegdGezagsByTypeId,
  useGetCompaniesReport,
  usePostAggregateLandelijkReport,
  useGetMilieudiensten,
  useGetRapportageArchiefRekenmotorVersies,
} from '@/lib/queries/reports';
import { downloadBlob } from '@/lib/utils/downloadBlob';
import { Button, Flex, Form, Select, Checkbox } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Conditional } from '../core/conditional/conditional';
import { CustomDatePicker } from '../core/custom-datepicker/CustomDatepicker';
import useApp from 'antd/es/app/useApp';

export const filterOption = (input: string, option: { label: string; value: string }): boolean =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const REPORT_TYPES = {
  GEWAS: 'gewas',
  ALL_COMPANIES: 'allebedrijven',
  GEMEENTE: 'gemeente',
  WATERSCHAP: 'waterschap',
  LANDELIJK: 'landelijk',
  MILIEUDIENST: 'milieudienst',
};
const REPORT_TYPE_IDS = {
  gemeente: 1,
  waterschap: 2,
  landelijk: 3,
  gewas: undefined,
  allebedrijven: undefined,
};

interface ReportSelectProps {
  showRekenmotorVersie?: boolean;
  selectedRekenmotorVersie?: string;
  selectedYear?: number;
}

interface ReportFormValues {
  year?: number | dayjs.Dayjs;
  type?: string;
  bevoegdGezagId?: number;
  landelijkSubtype?: string;
  landelijkIds?: number[];
  landelijkSelectAll?: boolean;
  rekenmotorVersie?: string;
}

interface ReportSelection {
  year?: number;
  type?: string;
  bevoegdGezagId?: number;
  landelijkSubtype?: string;
  landelijkIds?: number[];
  rekenmotorVersie?: string;
}

export const ReportSelect: React.FunctionComponent<ReportSelectProps> = ({ showRekenmotorVersie = false, selectedRekenmotorVersie, selectedYear }) => {
  const { t } = useTranslation('reports');
  const [form] = Form.useForm<ReportFormValues>();
  const { message } = useApp();
  const defaultYear = selectedYear ?? (showRekenmotorVersie ? null : dayjs().year() - 1);
  const [year, setYear] = useState<number | null>(defaultYear);
  const [type, setType] = useState<string | null>(null);
  const [landelijkSubtype, setLandelijkSubtype] = useState<string | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<number[]>([]);
  const [bevoegdGezagId, setBevoegdGezagId] = useState<number | null>(null);
  const [selection, setSelection] = useState<ReportSelection>({});
  const [landelijkSelectAll, setLandelijkSelectAll] = useState<boolean>(false);
  const { data: dataRekenmotorVersies, isLoading: isLoadingRekenmotorVersies } = useGetRapportageArchiefRekenmotorVersies(
    year ?? undefined,
    showRekenmotorVersie && !!year
  );

  const showAggregateReportBG = tokenContainsRole([Roles.Administrator, Roles.Bevoegdgezag]);
  const showAggregateReportGewas = tokenContainsRole([Roles.Administrator]);
  const showLandelijkAggregateReport = tokenContainsRole([Roles.Administrator]);

  const effectiveTypeForFetch = type === REPORT_TYPES.LANDELIJK ? (landelijkSubtype ?? '') : (type ?? '');
  const { isLoading: isLoadingBevoegdGezagen, data: dataBevoegdGezagen } = useGetBevoegdGezagsByTypeId(
    REPORT_TYPE_IDS[effectiveTypeForFetch as keyof typeof REPORT_TYPE_IDS],
    [REPORT_TYPES.GEMEENTE, REPORT_TYPES.WATERSCHAP, REPORT_TYPES.LANDELIJK].includes(type ?? '') &&
      REPORT_TYPE_IDS[effectiveTypeForFetch as keyof typeof REPORT_TYPE_IDS] !== undefined
  );
  const { isLoading: isLoadingMilieudiensten, data: dataMilieudiensten } = useGetMilieudiensten(
    type === REPORT_TYPES.LANDELIJK && landelijkSubtype === REPORT_TYPES.MILIEUDIENST
  );
  const landelijkOptions = getLandelijkOptions(landelijkSubtype, dataMilieudiensten, dataBevoegdGezagen);
  const landelijkPlaceholder = getLandelijkPlaceholder(landelijkSubtype);

  useEffect(() => {
    if (dataBevoegdGezagen?.length === 1) {
      setBevoegdGezagId(dataBevoegdGezagen[0].id);
      form.setFieldsValue({ bevoegdGezagId: dataBevoegdGezagen[0].id });
    }
  }, [dataBevoegdGezagen, form]);

  useEffect(() => {
    form.setFieldsValue({ year: defaultYear ?? undefined });
    setYear(defaultYear);
  }, [defaultYear, form]);

  useEffect(() => {
    form.setFieldsValue({ rekenmotorVersie: selectedRekenmotorVersie });
  }, [form, selectedRekenmotorVersie]);

  const { isLoading: isLoadingAllCompanies, data: dataAllCompanies } = useGetCompaniesReport(
    selection.year,
    !!selection.year && selection.type === REPORT_TYPES.ALL_COMPANIES,
    selection.rekenmotorVersie
  );
  const { isLoading: isLoadingAggGewas, data: dataAggGewas } = useGetAggregateGewasReport(
    selection.year,
    !!selection.year && selection.type === REPORT_TYPES.GEWAS,
    selection.rekenmotorVersie
  );
  const { isLoading: isLoadingAggOther, data: dataAggOther } = useGetAggregateOtherReport(
    {
      registratieJaar: selection.year,
      bevoegdGezagId: selection.bevoegdGezagId,
      bevoegdGezagTypeCode: selection.type,
      rekenmotorVersie: selection.rekenmotorVersie,
    },
    !!selection.bevoegdGezagId && !!selection.year && [REPORT_TYPES.GEMEENTE, REPORT_TYPES.WATERSCHAP].includes(selection.type ?? '')
  );

  const { isLoading: isLoadingAggLandelijk, data: dataAggLandelijk } = usePostAggregateLandelijkReport(
    selection.type === REPORT_TYPES.LANDELIJK
      ? {
          registratieJaar: selection.year,
          bevoegdGezagIds: selection.landelijkIds ?? [],
          bevoegdGezagType: selection.landelijkSubtype,
          rekenmotorVersie: selection.rekenmotorVersie,
        }
      : undefined,
    selection.type === REPORT_TYPES.LANDELIJK && !!selection.year && !!selection.landelijkSubtype && (selection.landelijkIds?.length ?? 0) > 0
  );
  const submitDisabled = isSubmitDisabled(year, type, bevoegdGezagId, landelijkSubtype, landelijkSelectAll, selectedEntities);

  const onFinish = (values: ReportFormValues) => {
    setSelection({
      year: parseReportYear(values.year),
      type: values.type,
      bevoegdGezagId: values.bevoegdGezagId,
      landelijkSubtype: values.landelijkSubtype,
      landelijkIds: values.landelijkSelectAll ? getAllLandelijkIds(landelijkSubtype, dataMilieudiensten, dataBevoegdGezagen) : values.landelijkIds,
      rekenmotorVersie: values.rekenmotorVersie,
    });
  };

  const onYearChange = (date: dayjs.Dayjs | null) => {
    setYear(date ? dayjs(date).year() : null);
    form.setFieldsValue({ rekenmotorVersie: undefined });
  };

  useEffect(() => {
    if (!selection.year || !selection.type) return;
    downloadSelectedReport(selection, { dataAggGewas, dataAllCompanies, dataAggLandelijk, dataAggOther }, message.warning);
    // Reset requestParams so it doesn't refetch on every render
    setSelection({});
  }, [dataAggGewas, dataAllCompanies, dataAggOther, dataAggLandelijk]);

  return (
    <Form onFinish={onFinish} form={form}>
      <Flex gap={8}>
        <Form.Item rules={[{ required: true }]} name="year">
          <CustomDatePicker picker="year" onChange={onYearChange} />
        </Form.Item>
        {showRekenmotorVersie && (
          <Form.Item name="rekenmotorVersie" style={{ width: 240 }}>
            <Select
              allowClear
              showSearch
              disabled={!year}
              loading={isLoadingRekenmotorVersies}
              placeholder={t('archive.rekenmotorVersion')}
              options={(dataRekenmotorVersies ?? []).map(version => ({ value: version, label: version }))}
            />
          </Form.Item>
        )}
        <Form.Item rules={[{ required: true }]} style={{ width: 200 }} name="type">
          <Select
            showSearch
            filterOption={(input, option) => filterOption(input, option as never)}
            optionFilterProp="children"
            options={[
              { value: 'gewas', label: 'Aggregatie gewas', disabled: !showAggregateReportGewas },
              { value: 'gemeente', label: 'Aggregatie gemeente', disabled: !showAggregateReportBG },
              { value: 'landelijk', label: 'Aggregatie landelijk', disabled: !showLandelijkAggregateReport },
              { value: 'waterschap', label: 'Aggregatie waterschap', disabled: !showAggregateReportBG },
              { value: 'allebedrijven', label: 'Alle bedrijven' },
            ]}
            placeholder={'Rapport type'}
            onChange={value => {
              setType(value);
              setBevoegdGezagId(null);
              setLandelijkSubtype(null);
              setSelectedEntities([]);
              form.setFieldsValue({ bevoegdGezagId: undefined, landelijkSubtype: undefined, landelijkIds: [] });
            }}
          />
        </Form.Item>
        <Conditional condition={['gemeente', 'waterschap'].includes(type ?? '')}>
          <Form.Item name="bevoegdGezagId">
            <Select
              loading={isLoadingBevoegdGezagen}
              disabled={isLoadingBevoegdGezagen}
              placeholder={`Selecteer een ${type}`}
              style={{ width: '300px' }}
              options={dataBevoegdGezagen
                ?.sort((a, b) => a.volledigeNaam!.localeCompare(b.volledigeNaam!))
                .map(bevoegdGezag => ({ value: bevoegdGezag.id, label: bevoegdGezag.volledigeNaam }))}
              onChange={value => setBevoegdGezagId(value)}
            />
          </Form.Item>
        </Conditional>
        <Conditional condition={type === 'landelijk'}>
          <Flex gap={8}>
            <Form.Item name="landelijkSubtype" rules={[{ required: true }]}>
              <Select
                placeholder={'Kies type'}
                style={{ width: 200 }}
                options={[
                  { value: REPORT_TYPES.GEMEENTE, label: 'Gemeente' },
                  { value: REPORT_TYPES.WATERSCHAP, label: 'Waterschap' },
                  { value: REPORT_TYPES.MILIEUDIENST, label: 'Milieudienst' },
                ]}
                onChange={value => {
                  setLandelijkSubtype(value);
                  setSelectedEntities([]);
                  setLandelijkSelectAll(false);
                  form.setFieldsValue({ landelijkIds: [], landelijkSelectAll: false });
                }}
              />
            </Form.Item>
            <Form.Item
              name="landelijkIds"
              rules={[
                {
                  required: true,
                  validator: async (_, value) => {
                    const selectAll = form.getFieldValue('landelijkSelectAll');
                    if (selectAll || (value && value.length > 0)) {
                      return;
                    }
                    throw new Error('Selecteer minstens één of kies alles');
                  },
                },
              ]}>
              <Select
                mode="multiple"
                loading={landelijkSubtype === REPORT_TYPES.MILIEUDIENST ? isLoadingMilieudiensten : isLoadingBevoegdGezagen}
                disabled={
                  (landelijkSubtype === REPORT_TYPES.MILIEUDIENST ? isLoadingMilieudiensten : isLoadingBevoegdGezagen) ||
                  !landelijkSubtype ||
                  landelijkSelectAll ||
                  form.getFieldValue('landelijkSelectAll') === true
                }
                placeholder={landelijkPlaceholder}
                style={{ minWidth: '300px' }}
                options={landelijkOptions}
                onChange={(values: number[]) => {
                  setSelectedEntities(values);
                  form.setFieldsValue({ landelijkIds: values });
                }}
              />
            </Form.Item>
            <Form.Item name="landelijkSelectAll" valuePropName="checked">
              <Checkbox
                disabled={isLoadingBevoegdGezagen || !landelijkSubtype}
                onChange={e => {
                  const checked = e.target.checked;
                  setLandelijkSelectAll(checked);
                  if (checked) {
                    // Clear visual selections when selecting all
                    setSelectedEntities([]);
                    form.setFieldsValue({ landelijkIds: [] });
                  }
                }}>
                Selecteer alles
              </Checkbox>
            </Form.Item>
          </Flex>
        </Conditional>
        <Button
          htmlType="submit"
          type="primary"
          disabled={submitDisabled}
          loading={isLoadingAllCompanies || isLoadingAggGewas || isLoadingAggOther || isLoadingAggLandelijk}>
          {t('actions.submit')}
        </Button>
      </Flex>
    </Form>
  );
};

const isSubmitDisabled = (
  year: number | null,
  type: string | null,
  bevoegdGezagId: number | null,
  landelijkSubtype: string | null,
  landelijkSelectAll: boolean,
  selectedEntities: number[]
): boolean => {
  if (!year || !type) {
    return true;
  }

  if (type === REPORT_TYPES.GEMEENTE && !bevoegdGezagId) {
    return true;
  }

  return type === REPORT_TYPES.LANDELIJK && (!landelijkSubtype || (!landelijkSelectAll && selectedEntities.length === 0));
};

const parseReportYear = (value?: number | dayjs.Dayjs): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }

  return value ? dayjs(value).year() : undefined;
};

const getAllLandelijkIds = (landelijkSubtype: string | null, milieudiensten?: { id: number }[], bevoegdGezagen?: { id: number }[]): number[] => {
  const entities = landelijkSubtype === REPORT_TYPES.MILIEUDIENST ? milieudiensten : bevoegdGezagen;
  return (entities ?? []).map(entity => entity.id);
};

const getLandelijkPlaceholder = (landelijkSubtype: string | null): string => {
  if (!landelijkSubtype) {
    return 'Selecteer type eerst';
  }

  const suffix = landelijkSubtype === REPORT_TYPES.MILIEUDIENST ? 'en' : 's';
  return `Selecteer ${landelijkSubtype}${suffix}`;
};

const getLandelijkOptions = (
  landelijkSubtype: string | null,
  milieudiensten?: { id: number; volledigeNaam?: string }[],
  bevoegdGezagen?: { id: number; volledigeNaam?: string }[]
) => {
  const entities = landelijkSubtype === REPORT_TYPES.MILIEUDIENST ? milieudiensten : bevoegdGezagen;
  return (entities ?? [])
    .toSorted((a, b) => (a.volledigeNaam ?? '').localeCompare(b.volledigeNaam ?? ''))
    .map(entity => ({ value: entity.id, label: entity.volledigeNaam }));
};

const downloadSelectedReport = (
  selection: ReportSelection,
  data: { dataAggGewas?: Blob; dataAllCompanies?: Blob; dataAggLandelijk?: Blob; dataAggOther?: Blob },
  showWarning: (content: string) => void
): void => {
  const report = getSelectedReport(selection, data);

  if (!report.blob) {
    showWarning('Geen bestand gevonden');
    return;
  }

  downloadBlob(report.blob, createReportFileName(report.prefix, selection.year, selection.rekenmotorVersie));
};

const getSelectedReport = (
  selection: ReportSelection,
  data: { dataAggGewas?: Blob; dataAllCompanies?: Blob; dataAggLandelijk?: Blob; dataAggOther?: Blob }
): { blob?: Blob; prefix: string } => {
  switch (selection.type) {
    case REPORT_TYPES.GEWAS:
      return { blob: data.dataAggGewas, prefix: 'aggregate-gewas' };
    case REPORT_TYPES.ALL_COMPANIES:
      return { blob: data.dataAllCompanies, prefix: 'bedrijven_rapportage' };
    case REPORT_TYPES.LANDELIJK:
      return { blob: data.dataAggLandelijk, prefix: `aggregate-landelijk_${selection.landelijkSubtype}` };
    default:
      return { blob: data.dataAggOther, prefix: `aggregate-${selection.type}` };
  }
};

const createReportFileName = (prefix: string, year?: number, rekenmotorVersie?: string): string => {
  const versionSuffix = rekenmotorVersie ? `_${rekenmotorVersie}` : '';
  return `${prefix}_${year}${versionSuffix}.xlsx`;
};
